import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container, Row, Col, Card, Button, Spinner, Alert, Form, Table } from 'react-bootstrap'
import { productionAPI } from '../api/productionAPI'
import { warehouseAPI } from '../api/warehouseAPI'
import { locationAPI } from '../api/locationAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'

function ProductionFinalizeScreen() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { hasPermission, loading: permissionsLoading } = usePermissions()

    const [productionLot, setProductionLot] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [finalizing, setFinalizing] = useState(false)

    // Output metadata
    const [bbDate, setBbDate] = useState('')
    const [actualQuantityProduced, setActualQuantityProduced] = useState('')

    // Usage tracking - keyed by allocation _id
    const [usageData, setUsageData] = useState({})
    // { allocation_id: { quantity_used: x, quantity_returned: y, return_location_id: z } }

    // Location selection state
    const [warehouses, setWarehouses] = useState([])
    const [locationsByWarehouse, setLocationsByWarehouse] = useState({})
    const [selectedWarehouses, setSelectedWarehouses] = useState({})
    // { allocation_id: warehouse_id }

    useEffect(() => {
        fetchProductionLot()
        fetchWarehouses()
    }, [id])

    // Permission check - must be after all hooks
    if (!permissionsLoading && !hasPermission('production.finalize')) {
        return (
            <Container className="py-4">
                <Alert variant="danger">
                    You don&apos;t have permission to finalize production. Required: <code>production.finalize</code>
                </Alert>
                <Button variant="outline-primary" onClick={() => navigate('/productions')}>
                    Back to Production LOTs
                </Button>
            </Container>
        )
    }

    const fetchProductionLot = async () => {
        try {
            setLoading(true)
            const data = await productionAPI.getById(id)

            if (data.status !== 'in_progress') {
                setError('This production LOT cannot be finalized (not in progress)')
                return
            }

            setProductionLot(data)
            setActualQuantityProduced(data.target_quantity.toString())

            // Initialize usage data with allocated quantities
            const initialUsage = {}
            if (data.lot_ingredients) {
                data.lot_ingredients.forEach(ingredient => {
                    if (ingredient.inventory_allocations) {
                        ingredient.inventory_allocations.forEach(allocation => {
                            initialUsage[allocation._id] = {
                                quantity_used: allocation.quantity_allocated.toString(),
                                quantity_returned: '0',
                                return_location_id: ''
                            }
                        })
                    }
                })
            }
            setUsageData(initialUsage)
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to load production LOT')
        } finally {
            setLoading(false)
        }
    }

    const fetchWarehouses = async () => {
        try {
            const data = await warehouseAPI.getAll()
            const activeWarehouses = Array.isArray(data)
                ? data.filter(w => w.is_active)
                : (data.results || []).filter(w => w.is_active)
            setWarehouses(activeWarehouses)
        } catch (error) {
            console.error('Error fetching warehouses:', error)
        }
    }

    const fetchLocationsForWarehouse = async (warehouseId, allocationId) => {
        if (locationsByWarehouse[warehouseId]) {
            return // Already fetched
        }

        try {
            const data = await locationAPI.getAll(warehouseId)
            const activeLocations = Array.isArray(data)
                ? data.filter(l => l.is_active)
                : (data.results || []).filter(l => l.is_active)
            setLocationsByWarehouse(prev => ({
                ...prev,
                [warehouseId]: activeLocations
            }))
        } catch (error) {
            console.error('Error fetching locations:', error)
        }
    }

    const handleWarehouseChange = (allocationId, warehouseId) => {
        setSelectedWarehouses(prev => ({
            ...prev,
            [allocationId]: warehouseId
        }))

        // Clear the location when warehouse changes
        setUsageData(prev => ({
            ...prev,
            [allocationId]: {
                ...prev[allocationId],
                return_location_id: ''
            }
        }))

        // Fetch locations for this warehouse if not already fetched
        if (warehouseId) {
            fetchLocationsForWarehouse(warehouseId, allocationId)
        }
    }

    const handleUsageChange = (allocationId, field, value) => {
        setUsageData(prev => ({
            ...prev,
            [allocationId]: {
                ...prev[allocationId],
                [field]: value
            }
        }))
    }

    const calculateTotalCost = () => {
        if (!productionLot) return 0

        let total = 0
        if (productionLot.lot_ingredients) {
            productionLot.lot_ingredients.forEach(ingredient => {
                if (ingredient.inventory_allocations) {
                    ingredient.inventory_allocations.forEach(allocation => {
                        const usage = usageData[allocation._id]
                        if (usage) {
                            const qtyUsed = parseFloat(usage.quantity_used) || 0
                            const costPerUnit = parseFloat(allocation.original_cost) || 0
                            total += qtyUsed * costPerUnit
                        }
                    })
                }
            })
        }
        return total
    }

    const calculateUnitCost = () => {
        const totalCost = calculateTotalCost()
        const qty = parseFloat(actualQuantityProduced) || 0
        if (qty <= 0) return 0
        return totalCost / qty
    }

    const validateFinalization = () => {
        if (!bbDate) {
            setError('Please enter a Best Before date')
            return false
        }
        if (!actualQuantityProduced || parseFloat(actualQuantityProduced) <= 0) {
            setError('Please enter the actual quantity produced')
            return false
        }

        // Validate usage data
        for (const [allocationId, usage] of Object.entries(usageData)) {
            const qtyUsed = parseFloat(usage.quantity_used) || 0
            const qtyReturned = parseFloat(usage.quantity_returned) || 0

            if (qtyUsed < 0 || qtyReturned < 0) {
                setError('Quantities cannot be negative')
                return false
            }

            if (qtyReturned > 0 && !usage.return_location_id) {
                setError('Please select a return location for all items being returned')
                return false
            }
        }

        return true
    }

    const handleFinalize = async () => {
        setError(null)

        if (!validateFinalization()) {
            return
        }

        setFinalizing(true)

        try {
            // Build usage array
            const usage = Object.entries(usageData).map(([allocationId, data]) => ({
                production_lot_inventory_item_id: parseInt(allocationId),
                quantity_used: parseFloat(data.quantity_used) || 0,
                quantity_returned: parseFloat(data.quantity_returned) || 0,
                return_location_id: data.return_location_id ? parseInt(data.return_location_id) : null
            }))

            const payload = {
                actual_quantity_produced: parseFloat(actualQuantityProduced),
                bb_date: bbDate,
                usage
            }

            await productionAPI.finalize(id, payload)
            navigate(`/production/${id}`)
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to finalize production')
        } finally {
            setFinalizing(false)
        }
    }

    if (loading) {
        return (
            <Container className="py-4">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        )
    }

    if (!productionLot) {
        return (
            <Container className="py-4">
                <Alert variant="danger">{error || 'Production lot not found'}</Alert>
                <Button variant="outline-primary" onClick={() => navigate('/productions')}>
                    Back to List
                </Button>
            </Container>
        )
    }

    const totalCost = calculateTotalCost()
    const unitCost = calculateUnitCost()

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h1>Finalize Production: {productionLot.lot_number}</h1>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Output Metadata */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Output Product Information</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>LOT Number</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={productionLot.lot_number}
                                    disabled
                                    readOnly
                                />
                                <Form.Text className="text-muted">
                                    LOT is set from production LOT number
                                </Form.Text>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Best Before Date <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    value={bbDate}
                                    onChange={(e) => setBbDate(e.target.value)}
                                />
                                <Form.Text className="text-muted">
                                    Country of origin is taken from the output product.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Actual Quantity Produced <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    value={actualQuantityProduced}
                                    onChange={(e) => setActualQuantityProduced(e.target.value)}
                                />
                                <Form.Text className="text-muted">
                                    Target was: {productionLot.target_quantity}
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Ingredient Usage */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Ingredient Usage</h5>
                </Card.Header>
                <Card.Body>
                    {productionLot.lot_ingredients && productionLot.lot_ingredients.map((lotIngredient) => (
                        <Card key={lotIngredient._id} className="mb-3">
                            <Card.Header>
                                <strong>{lotIngredient.ingredient_name}</strong>
                                <span className="text-muted ms-2">
                                    (Required: {lotIngredient.required_quantity}, Picked: {lotIngredient.picked_quantity})
                                </span>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <Table className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Inventory Item</th>
                                            <th>LOT</th>
                                            <th>Allocated</th>
                                            <th>Cost/Unit</th>
                                            <th style={{ width: '120px' }}>Qty Used</th>
                                            <th style={{ width: '120px' }}>Qty to Return</th>
                                            <th style={{ width: '150px' }}>Return Warehouse</th>
                                            <th style={{ width: '150px' }}>Return Location</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lotIngredient.inventory_allocations && lotIngredient.inventory_allocations.map((allocation) => {
                                            const usage = usageData[allocation._id] || {}
                                            const selectedWarehouse = selectedWarehouses[allocation._id] || ''
                                            const warehouseLocations = locationsByWarehouse[selectedWarehouse] || []
                                            const qtyReturned = parseFloat(usage.quantity_returned) || 0

                                            return (
                                                <tr key={allocation._id}>
                                                    <td>{allocation.inventory_item_data?.stock_item_description || '-'}</td>
                                                    <td>{allocation.inventory_item_data?.lot || '-'}</td>
                                                    <td>{allocation.quantity_allocated}</td>
                                                    <td>${parseFloat(allocation.original_cost).toFixed(2)}</td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.0001"
                                                            min="0"
                                                            max={allocation.quantity_allocated}
                                                            size="sm"
                                                            value={usage.quantity_used || ''}
                                                            onChange={(e) => handleUsageChange(
                                                                allocation._id,
                                                                'quantity_used',
                                                                e.target.value
                                                            )}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.0001"
                                                            min="0"
                                                            max={allocation.quantity_allocated}
                                                            size="sm"
                                                            value={usage.quantity_returned || ''}
                                                            onChange={(e) => handleUsageChange(
                                                                allocation._id,
                                                                'quantity_returned',
                                                                e.target.value
                                                            )}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Select
                                                            size="sm"
                                                            value={selectedWarehouse}
                                                            onChange={(e) => handleWarehouseChange(allocation._id, e.target.value)}
                                                            disabled={qtyReturned <= 0}
                                                        >
                                                            <option value="">Select...</option>
                                                            {warehouses.map(wh => (
                                                                <option key={wh._id} value={wh._id}>
                                                                    {wh.name}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </td>
                                                    <td>
                                                        <Form.Select
                                                            size="sm"
                                                            value={usage.return_location_id || ''}
                                                            onChange={(e) => handleUsageChange(
                                                                allocation._id,
                                                                'return_location_id',
                                                                e.target.value
                                                            )}
                                                            disabled={qtyReturned <= 0 || !selectedWarehouse}
                                                        >
                                                            <option value="">Select...</option>
                                                            {warehouseLocations.map(loc => (
                                                                <option key={loc._id} value={loc._id}>
                                                                    {loc.name}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    ))}
                </Card.Body>
            </Card>

            {/* Cost Summary */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Cost Summary</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={4}>
                            <p><strong>Total Cost:</strong> ${totalCost.toFixed(2)}</p>
                        </Col>
                        <Col md={4}>
                            <p><strong>Unit Cost:</strong> ${unitCost.toFixed(2)}</p>
                        </Col>
                        <Col md={4}>
                            <p><strong>Quantity:</strong> {actualQuantityProduced || 0}</p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Action Buttons */}
            <div className="d-flex gap-2">
                <Button
                    variant="success"
                    onClick={handleFinalize}
                    disabled={finalizing}
                >
                    {finalizing ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Finalizing...
                        </>
                    ) : (
                        'Complete Production'
                    )}
                </Button>
                <Button
                    variant="outline-secondary"
                    onClick={() => navigate(`/production/${id}`)}
                    disabled={finalizing}
                >
                    Cancel
                </Button>
            </div>
        </Container>
    )
}

export default ProductionFinalizeScreen
